import "../administration.css";

import {
  createContract,
  deleteContract,
  fetchCompanyContracts,
  resetContractAlert,
  updateContractAlert,
  updateContractInformation,
  updateContractsCurrentUser,
} from "../../actions/company";
import { nextSort, sortRows } from "../sortable";

import { AdminTable } from "../AdminTable";
import { Button } from "reactstrap";
import { ButtonAddRemoveUser } from "../../components/ButtonAddRemoveUser";
import ContractModal from "./ContractModal";
import CopyButton from "../../components/copyButton";
import { FilterBar } from "../../clientDashboard/components/FilterBar";
import { LevelHeader } from "../../clientDashboard/components/LevelHeader";
import { OPTIONS_SYSTEM } from "../../constants";
import PropTypes from "prop-types";
import React from "react";
import { CfSelect as Select } from "../../components/CfSelect";
import _ from "lodash";
import { connect } from "react-redux";
import { fetchCompanies } from "../../actions/companies";
import { fetchUsers } from "../../actions/users";
import { hasAdminAccess } from "../../services/roles";

class AdministrationCompany extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      updatedContract: {},
      // Accepted values: "", "contract"
      currentModal: "",
      search: "",
      systemFilter: [],
      year: undefined,
      dateFrom: "",
      dateTo: "",
      sortKey: "name",
      sortDir: "asc",
    };
    this.openContractModalForCreation =
      this.openContractModalForCreation.bind(this);
  }

  resetState() {
    this.setState({
      updatedContract: {},
      currentModal: "",
    });
  }

  componentDidMount() {
    this.props.fetchUsers();
    if (!this.props.company) {
      this.props.fetchCompanies(this.props.user);
      this.props.fetchCompanyContracts({ id: this.props.companyId });
    } else {
      this.props.fetchCompanyContracts(this.props.company);
    }
  }

  handleContractRowClick(company, contract) {
    this.props.history.push(
      `/administration/company/${company.id}/contract/${contract.id}`
    );
  }

  openContractModalForUpdate(event, contract, modalType) {
    event.stopPropagation();
    this.setState({
      updatedContract: contract,
      currentModal: modalType,
    });
  }

  /** CONTRACT MODAL */

  openContractModalForCreation(event) {
    event.stopPropagation();
    this.setState({
      updatedContract: {
        active: false,
        name: "",
        start_date: "",
        end_date: "",
      },
      currentModal: "contract",
    });
  }

  submitContractModal = (contract) => {
    if (this.checkContractUnicity(contract)) {
      const promise = contract.id
        ? this.props.onUpdateContract(this.props.company, contract)
        : this.props.onCreateContract(this.props.company, contract);

      promise
        .then(() => this.resetState())
        .catch((err) => this.props.updateContractAlert(err));
    }
  };

  checkContractUnicity(contract) {
    const contractsFiltered = _.filter(
      this.props.company.contracts,
      (item) => item.id !== contract.id
    );
    if (_.find(contractsFiltered, { name: contract.name })) {
      this.props.updateContractAlert("Campaign name must be unique.");
      return false;
    }
    return true;
  }

  cancelContractModal = () => {
    this.props.resetContractAlert();
    this.resetState();
  };

  deleteContract(event, contract) {
    event.stopPropagation();
    if (
      window.confirm(
        "Do you really want to delete campaign: " + contract.name + " ?"
      )
    )
      this.props.deleteContract(this.props.company, contract);
  }

  // Does the current user already have access to this campaign?
  hasAccess(contract) {
    return !!contract.users?.find(({ id }) => id === this.props.user.self.id);
  }

  companyMetrics() {
    const contracts = this.props.contracts || [];
    const sum = (key) => contracts.reduce((t, c) => t + (c[key] || 0), 0);
    return [
      { value: contracts.length, label: "Campaigns" },
      { value: sum("total_sites"), label: "Sites" },
    ];
  }

  systemOptions() {
    // Only offer systems actually present among this company's campaigns.
    const present = new Set((this.props.contracts || []).map((c) => c.system));
    return OPTIONS_SYSTEM.filter((option) => present.has(option.value));
  }

  // Years actually present in the data, newest first: no hardcoded range to
  // maintain.
  yearOptions() {
    const years = new Set();
    (this.props.contracts || []).forEach(({ start_date, end_date }) => {
      [start_date, end_date].forEach((date) => {
        if (date) years.add(Number(String(date).slice(0, 4)));
      });
    });
    return [...years]
      .sort((a, b) => b - a)
      .map((year) => ({ value: year, label: String(year) }));
  }

  // A year is a shortcut on the two date bounds, not a third criterion: it
  // writes the window and the overlap test above does the rest. Editing a date
  // by hand clears it, or the select would claim a year the bounds no longer
  // describe.
  selectYear(year) {
    this.setState(
      year == null
        ? { year: undefined, dateFrom: "", dateTo: "" }
        : { year, dateFrom: `${year}-01-01`, dateTo: `${year}-12-31` }
    );
  }

  sortValue(contract, key) {
    switch (key) {
      case "system":
        return (
          OPTIONS_SYSTEM.find((o) => o.value === contract.system)?.label ?? ""
        );
      case "start_date":
        return contract.start_date || "";
      case "end_date":
        return contract.end_date || "";
      case "total_microplots":
        return contract.total_microplots ?? 0;
      case "total_sites":
        return contract.total_sites ?? 0;
      case "users":
        return contract.users?.length ?? 0;
      case "active":
        return contract.active ? 1 : 0;
      default:
        return contract.name || "";
    }
  }

  // Filtered + sorted campaigns — single source of truth for both the table
  // and the bulk access buttons.
  visibleContracts() {
    const { search, systemFilter, dateFrom, dateTo, sortKey, sortDir } =
      this.state;
    const needle = search.trim().toLowerCase();
    const filtered = (this.props.contracts || []).filter((contract) => {
      const matchesQuery =
        !needle || contract.name.toLowerCase().includes(needle);
      const matchesSystem =
        systemFilter.length === 0 || systemFilter.includes(contract.system);
      // Overlap, not containment: a campaign running from October 2025 to June
      // 2026 was active in 2025 and must answer a 2025 filter. It used to need
      // to fit entirely inside the window, so anything straddling the boundary
      // vanished (R4-02). A missing bound is open ended in that direction, so it
      // never excludes on its own.
      const matchesFrom =
        !dateFrom || !contract.end_date || contract.end_date >= dateFrom;
      const matchesTo =
        !dateTo || !contract.start_date || contract.start_date <= dateTo;
      return matchesQuery && matchesSystem && matchesFrom && matchesTo;
    });
    return sortRows(filtered, sortKey, sortDir, (c, k) => this.sortValue(c, k));
  }

  handleSort = (column) => {
    this.setState((prev) => nextSort(prev, column));
  };

  grantAllAccess = () => {
    const ids = this.visibleContracts()
      .filter((c) => !this.hasAccess(c))
      .map((c) => c.id);
    if (ids.length === 0) return;
    this.props.updateContractsCurrentUser(ids, this.props.company);
  };

  removeAllAccess = () => {
    const ids = this.visibleContracts()
      .filter((c) => this.hasAccess(c))
      .map((c) => c.id);
    if (ids.length === 0) return;
    if (!window.confirm("Remove your access to all the campaigns shown?"))
      return;
    this.props.updateContractsCurrentUser(ids, this.props.company);
  };

  columns() {
    return [
      {
        key: "active",
        label: "Active",
        sortable: true,
        cell: (contract) =>
          contract.active ? (
            <i
              className="fa-solid fa-lg fa-check text-success"
              aria-hidden="true"
            />
          ) : (
            <i
              className="fa-solid fa-lg fa-xmark text-danger"
              aria-hidden="true"
            />
          ),
      },
      { key: "name", label: "Name", className: "w-25", sortable: true },
      {
        key: "system",
        label: "System",
        sortable: true,
        cell: (contract) =>
          OPTIONS_SYSTEM.find((item) => item.value === contract.system)
            ?.label ?? "",
      },
      { key: "start_date", label: "Start date", sortable: true },
      { key: "end_date", label: "End date", sortable: true },
      { key: "total_microplots", label: "Microplots", sortable: true },
      { key: "total_sites", label: "Sites", sortable: true },
      {
        key: "users",
        label: "Users",
        sortable: true,
        cell: (contract) => (
          <>
            {contract.users.length}
            <Button
              data-tooltip-id="tooltip"
              data-tooltip-content="Update campaign users"
              className="ms-2 p-0"
              color="link"
              onClick={(event) => {
                event.stopPropagation();
                this.props.history.push(
                  `/administration/company/${this.props.company.id}/contract/${contract.id}/users`
                );
              }}
            >
              <i className="fa-solid fa-lg fa-users" />
            </Button>
          </>
        ),
      },
      {
        key: "actions",
        label: "Actions",
        cell: (contract) => (
          <div className="admin-actions">
            <ButtonAddRemoveUser
              contract={contract}
              company={this.props.company}
              hasContractAccess={this.hasAccess(contract)}
            />
            <CopyButton value={contract.id} datatip="Copy campaign UUID" />
            {hasAdminAccess(this.props.user.self) && (
              <Button
                data-tooltip-id="tooltip"
                data-tooltip-content="Edit campaign information"
                color="link"
                onClick={(event) =>
                  this.openContractModalForUpdate(event, contract, "contract")
                }
              >
                <i className="fa-solid fa-lg fa-pencil" />
              </Button>
            )}
            <Button
              data-tooltip-id="tooltip"
              data-tooltip-content="View upload sessions"
              color="link"
              onClick={(e) => {
                e.stopPropagation();
                this.props.history.push(
                  "/uploadsessions?company=" +
                    this.props.company.id +
                    "&contract=" +
                    contract.id
                );
              }}
            >
              <i className="fa-solid fa-lg fa-upload" />
            </Button>
            {contract.active ? (
              <Button
                data-tooltip-id="tooltip"
                data-tooltip-content="Deactivate the campaign before deleting"
                data-tooltip-place="bottom"
                className="admin-actions__off"
                color="link"
                onClick={(event) => event.stopPropagation()}
              >
                <i className="fa-solid fa-lg fa-trash-can" />
              </Button>
            ) : (
              <Button
                data-tooltip-id="tooltip"
                data-tooltip-content="Delete campaign"
                data-tooltip-variant="error"
                data-tooltip-place="bottom"
                className="admin-actions__danger"
                color="link"
                onClick={(event) => this.deleteContract(event, contract)}
              >
                <i className="fa-solid fa-lg fa-trash-can" />
              </Button>
            )}
          </div>
        ),
      },
    ];
  }

  // Grant/remove act on the *shown* campaigns (visibleContracts), so the
  // buttons live next to the filter bar — position communicates scope — and the
  // count in the label removes any doubt about how many campaigns are affected.
  renderFilterActions() {
    const visible = this.visibleContracts();
    const grantable = visible.filter((c) => !this.hasAccess(c)).length;
    const removable = visible.filter((c) => this.hasAccess(c)).length;
    return (
      <>
        {hasAdminAccess(this.props.user.self) && (
          <Button
            color="primary"
            size="sm"
            onClick={this.openContractModalForCreation}
          >
            <i className="fa-solid fa-plus me-1" /> New campaign
          </Button>
        )}
        <Button
          color="success"
          outline
          size="sm"
          disabled={grantable === 0}
          onClick={this.grantAllAccess}
        >
          <i className="fa-solid fa-user-plus me-1" /> Grant my access (
          {grantable})
        </Button>
        <Button
          color="danger"
          outline
          size="sm"
          disabled={removable === 0}
          onClick={this.removeAllAccess}
        >
          <i className="fa-solid fa-user-xmark me-1" /> Remove my access (
          {removable})
        </Button>
      </>
    );
  }

  renderFilters() {
    return (
      <FilterBar
        query={this.state.search}
        onQuery={(q) => this.setState({ search: q })}
        placeholder="Search a campaign…"
        selects={[
          {
            label: "System",
            value: this.state.systemFilter,
            onChange: (vals) => this.setState({ systemFilter: vals }),
            allLabel: "All systems",
            options: this.systemOptions(),
          },
        ]}
        extra={
          <>
            <label className="cf-field">
              <span className="cf-field__label">Year</span>
              <Select
                className="cf-rs"
                classNamePrefix="cf-rs"
                isClearable
                isSearchable={false}
                placeholder="Any year"
                options={this.yearOptions()}
                value={
                  this.yearOptions().find(
                    (option) => option.value === this.state.year
                  ) ?? null
                }
                onChange={(option) => this.selectYear(option?.value)}
              />
            </label>
            <label className="cf-field">
              <span className="cf-field__label">Dates</span>
              <div className="d-flex align-items-center gap-1">
                <input
                  type="date"
                  className="cf-date"
                  value={this.state.dateFrom}
                  onChange={(e) =>
                    this.setState({ dateFrom: e.target.value, year: undefined })
                  }
                  aria-label="Start date from"
                />
                <span className="text-muted">→</span>
                <input
                  type="date"
                  className="cf-date"
                  value={this.state.dateTo}
                  onChange={(e) =>
                    this.setState({ dateTo: e.target.value, year: undefined })
                  }
                  aria-label="End date to"
                />
              </div>
            </label>
          </>
        }
        trailing={this.renderFilterActions()}
      />
    );
  }

  renderCompanyCard() {
    return (
      <>
        {this.renderFilters()}
        <AdminTable
          columns={this.columns()}
          rows={this.visibleContracts()}
          loading={this.props.refreshingContracts}
          sortKey={this.state.sortKey}
          sortDir={this.state.sortDir}
          onSort={this.handleSort}
          onRowClick={(contract) =>
            this.handleContractRowClick(this.props.company, contract)
          }
        />
      </>
    );
  }

  render() {
    if (!this.props.company) return "";

    return (
      <>
        {this.state.currentModal === "contract" && (
          <ContractModal
            alert={this.props.contractUpdateAlert}
            isOpen
            onClose={this.cancelContractModal}
            onSubmit={this.submitContractModal}
            contract={this.state.updatedContract}
            users={this.props.users}
          />
        )}
        <div className="page-content container-fluid cf-page">
          <LevelHeader
            path={[
              { label: "Administration", to: "/administration" },
              { label: this.props.company.name },
            ]}
            metrics={this.companyMetrics()}
          />
          {this.renderCompanyCard()}
        </div>
      </>
    );
  }
}

function mapStateToProps(store, props) {
  const { companyId } = props.match.params;
  const companies = store.companies.all;
  let company = {};

  if (companies) company = companies.find((item) => item.id === companyId);

  return {
    user: store.user,
    companyId: companyId,
    company: company,
    contracts: store.company.contracts,
    contractUpdateAlert: store.company.contractUpdateAlert,
    refreshingContracts: store.company.contractsTable.refreshing,
  };
}

const callbacks = {
  onUpdateContract: updateContractInformation,
  onCreateContract: createContract,
  updateContractAlert,
  resetContractAlert,
  deleteContract,
  fetchCompanyContracts,
  fetchCompanies,
  updateContractsCurrentUser,
  fetchUsers,
};

export default connect(mapStateToProps, callbacks)(AdministrationCompany);

AdministrationCompany.propTypes = {
  fetchCompanies: PropTypes.func.isRequired,
  fetchCompanyContracts: PropTypes.func.isRequired,
  refreshingContracts: PropTypes.bool.isRequired,
  onUpdateContract: PropTypes.func.isRequired,
  onCreateContract: PropTypes.func.isRequired,
  updateContractsCurrentUser: PropTypes.func.isRequired,
  resetContractAlert: PropTypes.func.isRequired,
  contractUpdateAlert: PropTypes.string.isRequired,
  updateContractAlert: PropTypes.func.isRequired,
  fetchUsers: PropTypes.func.isRequired,
};
